import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { MessagesSquare, Sparkles, Zap, Filter } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function Conversations() {
  const [list, setList] = useState([]);
  const [agents, setAgents] = useState([]);
  const [agentFilter, setAgentFilter] = useState("all");
  const [billing, setBilling] = useState(null);
  const [selected, setSelected] = useState(null);
  const [convo, setConvo] = useState(null);

  const loadConvos = (aid = agentFilter) =>
    api.get("/chat/conversations", { params: aid && aid !== "all" ? { agent_id: aid } : {} })
      .then((r) => setList(r.data));

  useEffect(() => {
    api.get("/agents").then((r) => setAgents(r.data || []));
    api.get("/billing/me").then((r) => setBilling(r.data));
    loadConvos("all");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { loadConvos(agentFilter); /* eslint-disable-next-line */ }, [agentFilter]);

  const open = async (id) => {
    setSelected(id);
    const { data } = await api.get(`/chat/conversations/${id}`);
    setConvo(data);
  };

  const summarize = async () => {
    if (!selected) return;
    const t = toast.loading("Summarizing…");
    try {
      await api.post(`/chat/conversations/${selected}/summarize`);
      toast.success("Summarized", { id: t });
      loadConvos(agentFilter);
      open(selected);
    } catch { toast.error("Failed", { id: t }); }
  };

  const totals = useMemo(() => ({
    spent: list.reduce((acc, c) => acc + (c.credits_spent || 0), 0),
    convos: list.length,
    messages: list.reduce((acc, c) => acc + (c.message_count || 0), 0),
  }), [list]);

  const aiTotal = billing ? (billing.ai_credits || 0) + (billing.ai_credits_used || 0) : 0;

  return (
    <div data-testid="conversations-page" className="space-y-6">
      <div>
        <h1 className="font-display font-extrabold text-3xl">Conversations</h1>
        <p className="text-sm text-[#4B5563]">All chat sessions across your agents — filter by agent and review credit spend per session.</p>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="hs-card p-4">
          <div className="text-[10px] font-mono uppercase text-[#4B5563]">conversations</div>
          <div className="font-display font-extrabold text-2xl mt-1">{totals.convos}</div>
        </div>
        <div className="hs-card p-4">
          <div className="text-[10px] font-mono uppercase text-[#4B5563]">messages</div>
          <div className="font-display font-extrabold text-2xl mt-1">{totals.messages}</div>
        </div>
        <div className="hs-card p-4">
          <div className="text-[10px] font-mono uppercase text-[#4B5563] flex items-center gap-1"><Zap className="w-3 h-3" /> credits spent (filtered)</div>
          <div className="font-display font-extrabold text-2xl mt-1">{totals.spent}</div>
        </div>
        <div className="hs-card p-4">
          <div className="text-[10px] font-mono uppercase text-[#4B5563]">remaining / total</div>
          <div className="font-display font-extrabold text-2xl mt-1">
            {billing?.ai_credits ?? "—"}
            <span className="font-mono font-normal text-base text-[#4B5563]"> / {aiTotal || "—"}</span>
          </div>
          <div className="text-[10px] text-[#4B5563] mt-0.5">AI credits this cycle</div>
        </div>
      </div>

      {/* Filter bar */}
      <div className="hs-card p-3 flex items-center gap-3">
        <Filter className="w-4 h-4 text-[#4B5563]" />
        <label className="text-[10px] font-mono uppercase text-[#4B5563]">Agent</label>
        <select
          data-testid="conv-agent-filter"
          value={agentFilter}
          onChange={(e) => setAgentFilter(e.target.value)}
          className="border border-[#E2E8F0] rounded-sm bg-white text-xs px-2 py-1.5 min-w-[220px]"
        >
          <option value="all">All agents ({agents.length})</option>
          {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
        {agentFilter !== "all" && (
          <button onClick={() => setAgentFilter("all")} className="text-[11px] text-[#0044FF] hover:underline" data-testid="conv-clear-filter">clear filter</button>
        )}
      </div>

      {list.length === 0 ? (
        <div className="hs-card p-12 text-center">
          <MessagesSquare className="w-10 h-10 mx-auto text-[#4B5563]" />
          <div className="font-display font-bold mt-4">No conversations yet</div>
          <div className="text-sm text-[#4B5563]">Test your agent or embed the widget to start.</div>
        </div>
      ) : (
        <div className="hs-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#F3F4F6] text-[11px] uppercase font-mono text-[#4B5563]">
              <tr>
                <th className="text-left px-4 py-3">Agent</th>
                <th className="text-left px-4 py-3">Channel</th>
                <th className="text-left px-4 py-3">Preview</th>
                <th className="text-left px-4 py-3">Started</th>
                <th className="text-right px-4 py-3">Msgs</th>
                <th className="text-right px-4 py-3">Credits</th>
                <th className="text-left px-4 py-3">Sentiment</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {list.map((c) => (
                <tr key={c.id} className="border-t border-[#E2E8F0] hover:bg-[#F9FAFB]" data-testid={`conv-row-${c.id}`}>
                  <td className="px-4 py-3 text-xs font-medium truncate max-w-[160px]" title={c.agent_name}>{c.agent_name}</td>
                  <td className="px-4 py-3 font-mono text-xs">{c.channel}</td>
                  <td className="px-4 py-3 max-w-[360px] truncate">{c.preview}</td>
                  <td className="px-4 py-3 text-xs text-[#4B5563]">{c.started_at?.slice(0, 16).replace("T", " ")}</td>
                  <td className="px-4 py-3 text-right">{c.message_count}</td>
                  <td className="px-4 py-3 text-right font-mono text-[#0044FF] font-bold" data-testid={`conv-credits-${c.id}`}>{c.credits_spent || 0}</td>
                  <td className="px-4 py-3 text-xs">{c.sentiment || "—"}</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => open(c.id)} className="text-xs text-[#0044FF] font-medium" data-testid={`conv-open-${c.id}`}>Open →</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!selected} onOpenChange={(v) => { if (!v) { setSelected(null); setConvo(null); } }}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>Conversation</span>
              <button onClick={summarize} className="btn-outline text-xs flex items-center gap-1" data-testid="conv-summarize-button"><Sparkles className="w-3 h-3" /> AI summary</button>
            </DialogTitle>
          </DialogHeader>
          {convo && (
            <div>
              {convo.summary && (
                <div className="hs-card p-3 bg-[#F9FAFB] mb-3 text-sm">
                  <div className="font-mono text-[10px] uppercase text-[#4B5563]">summary · sentiment: {convo.sentiment}</div>
                  <div className="mt-1">{convo.summary}</div>
                  {convo.action_items?.length > 0 && <div className="mt-2 text-xs"><b>Action:</b> {convo.action_items.join("; ")}</div>}
                </div>
              )}
              <div className="space-y-2 max-h-[420px] overflow-auto">
                {convo.messages?.map((m, i) => (
                  <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[85%] px-3 py-2 text-sm rounded-sm ${m.role === "user" ? "bg-[#0044FF] text-white" : "bg-[#F3F4F6]"}`}>{m.content}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
